import { observer } from 'mobx-react-lite';

import { IconAppConnect } from '@components/icons/app-connect';
import { IconBrandADManager } from '@components/icons/brand-ad-manager';
import { IconBrandH3 } from '@components/icons/brand-h3';
import { IconBrandHireflix } from '@components/icons/brand-hireflix';
import { IconBrandJourneys } from '@components/icons/brand-journeys';
import { IconBulk } from '@components/icons/bulk';
import { IconDownload } from '@components/icons/download';
import { IconDropdown } from '@components/icons/dropdown';
import { IconEmail } from '@components/icons/email';
import { IconMove } from '@components/icons/move';
import { IconOnboarding } from '@components/icons/onboarding';
import { IconPersonAdd } from '@components/icons/person-add';
import { IconReject } from '@components/icons/reject';
import { IconSend } from '@components/icons/send';
import { IconStages } from '@components/icons/stages';
import { IconStatus } from '@components/icons/status';
import { IconTada } from '@components/icons/tada';
import { IconTrash } from '@components/icons/trash';
import { IconUndo } from '@components/icons/undo';
import { Menu } from '@components/menu';
import { SelectionBanner } from '@components/table/selection-banner';
import { useScopedTranslate } from '@locale/translate';

import { useFeature } from 'features/authentication';
import { useModal } from 'features/modals';
import { usePermissions } from 'features/permissions';
import { useDeleteApplicantsAccess } from 'hooks/use-delete-applicants-access';
import { useADManagerEnabled } from 'integrations/adManager/hooks';
import { useBulkCreateH3CoreSidebar } from 'integrations/H3core/bulk-create-sidebar';
import { useBulkEditH3CoreSidebar } from 'integrations/H3core/bulk-edit-sidebar';
import { useIsH3CoreEnabled } from 'integrations/H3core/hooks';
import { useHireflixEnabled } from 'integrations/hireflix/hooks';
import { useAddToJourneySidebar } from 'integrations/journeys/add-to-journey-sidebar';
import { useIsJourneysEnabled } from 'integrations/journeys/hooks';
import { useStageEntryJourneyPrompt } from 'integrations/journeys/job-triggers/use-stage-entry-journey';
import { useIsOnboardingFormEnabled } from 'integrations/onboardingForm/api/hooks';
import type { IApplication, IJob } from 'models/types';
import {
  exportListScope,
  useExportApplicantsSidebar,
} from 'pages/Job/sidebars/export-applicants-sidebar';
import { ApplicationRejectionMethod } from 'types/applications';

const ICON_COLOR = 'elementPurple80';

export const JobBulkActions = observer(({ job }: { job: IJob }) => {
  const { t } = useScopedTranslate(locale);
  const { showModal } = useModal();
  const { hasRolePermission } = usePermissions();
  const deleteAccess = useDeleteApplicantsAccess()(job.createdById?.id);
  const showExportApplicantsSidebar = useExportApplicantsSidebar();
  const { promptStageEntryJourney } = useStageEntryJourneyPrompt(job.id);
  const selected = job.listView.selectedApplications as Array<IApplication>;

  const hiredApplications = selected.filter((application) => application.status === 'hired');
  const activeApplications = selected.filter((application) => application.status === 'active');
  const rejectableApplications = selected.filter(
    (application) => application.status === 'active' || application.status === 'rejected',
  );
  const nonActiveApplications = selected.filter((application) => application.status !== 'active');

  const onboardingFormEnabled =
    useIsOnboardingFormEnabled() && hasRolePermission('application:bulk-send-onboarding');
  const hireflixEnabled =
    useHireflixEnabled(job.id) && hasRolePermission('application:bulk-send-hireflix');
  const adManagerEnabled =
    useADManagerEnabled(job.id) && hasRolePermission('application:bulk-send-admanager');
  const h3CoreEnabled = useIsH3CoreEnabled() && hasRolePermission('application:bulk-edit-h3core');
  const canViewJourneys =
    useFeature('FEATURE_INTEGRATION_JOURNEYS') &&
    hasRolePermission('application:bulk-create-journeys');
  const isJourneysEnabled = useIsJourneysEnabled();

  const hasIntegrations =
    hireflixEnabled || adManagerEnabled || h3CoreEnabled || h3CoreEnabled || canViewJourneys;

  const h3NotCreated = hiredApplications.filter(
    (application) => !application.integrationsResults || !application.integrationsResults.h3core,
  );

  function bulkMessage() {
    // Message applicants modal isn't typed so it doesn't know
    // applicant and applicants should not both exist.
    // @ts-expect-error
    showModal('MESSAGE_APPLICANTS', {
      _container: {
        width: '37.5rem',
      },
      applicants: selected,
    });
  }
  function bulkGrant() {
    showModal('GRANT_MULTIPLE', {
      _container: {
        width: '37.5rem',
      },
      applicants: selected,
      job,
    });
  }
  function bulkSendOnboardingForm() {
    showModal('INTEGRATION_ONBOARDING_FORM_SEND', {
      applicants: hiredApplications,
    });
  }
  function bulkMoveToStage() {
    const applicationIds = selected.map((applicant) => applicant.id);
    showModal('MOVE_TO_STAGE', {
      _container: {
        width: '37.5rem',
      },
      applicants: selected,
      job,
      onSuccess: (newStage: number) => {
        job.listView.updateStageForSelection(newStage);
        promptStageEntryJourney(applicationIds, newStage);
      },
    });
  }
  function bulkHireflixInvite() {
    showModal('INTEGRATION_HIREFLIX_BULK_SEND_INTERVIEW', {
      applicants: selected,
      job,
    });
  }
  async function bulkADManagerCreateUser() {
    showModal('INTEGRATION_ADMANAGER_BULK_CREATE_USER', {
      applicationIDs: selected.map((applicant) => applicant.id),
      jobID: job.id,
    });
  }
  function bulkReject() {
    // Because the Reject modal isn't typed, it doesn't know only
    // `applicant` or `applicants` should be passed but not both.
    // Once the component is converted to TS, it will no longer complain
    // about missing `applicant` here.
    // @ts-expect-error
    showModal('REJECT_APPLICANTS', {
      _container: {
        width: '37.5rem',
      },
      applicants: rejectableApplications,
      onSuccess: (rejectionMethod: ApplicationRejectionMethod) => {
        job.listView.updateRejectionForSelection(rejectionMethod);
      },
    });
  }
  function bulkHire() {
    showModal('BULK_HIRE_APPLICANTS', {
      applicants: activeApplications,
    });
  }
  function bulkDelete() {
    showModal('BULK_DELETE_APPLICANTS', {
      applicants: selected,
    });
  }

  function bulkRetract() {
    showModal('BULK_RETRACT_APPLICANTS', {
      applicants: activeApplications,
    });
  }

  function bulkActive() {
    showModal('BULK_ACTIVE_APPLICANTS', {
      applicants: nonActiveApplications,
    });
  }

  const showH3BulkEditSidebar = useBulkEditH3CoreSidebar();
  const showH3BulkCreateSidebar = useBulkCreateH3CoreSidebar();
  const showJourneysCreateSidebar = useAddToJourneySidebar();

  if (!hasRolePermission('applicants:use-bulk-actions')) {
    return null;
  }
  return (
    <SelectionBanner selectedCount={selected.length} clearSelected={job.listView.clearSelected}>
      <Menu
        buttonProps={{
          variant: 'ghost',
          iconRight: <IconDropdown color={ICON_COLOR} />,
          iconLeft: <IconBulk color={ICON_COLOR} />,
          text: t('actions'),
        }}
      >
        {hasRolePermission('job:export') && (
          <Menu.Item
            onSelect={() =>
              showExportApplicantsSidebar({
                job: job,
                applicationIds: selected.map((applicant) => applicant.id),
                listScope: exportListScope(job),
              })
            }
            label={t('exportApplicants')}
            icon={<IconDownload color={ICON_COLOR} />}
          />
        )}
        <Menu.Item
          onSelect={bulkMessage}
          label={t('sendEmail')}
          icon={<IconEmail color={ICON_COLOR} />}
        />
        {hasRolePermission('application:grant-access') && (
          <Menu.Item
            onSelect={bulkGrant}
            label={t('grantAccess')}
            icon={<IconPersonAdd color={ICON_COLOR} />}
          />
        )}
        {onboardingFormEnabled && (
          <Menu.Item
            onSelect={() => (hiredApplications.length > 0 ? bulkSendOnboardingForm() : null)}
            label={t('sendOnboarding')}
            icon={<IconOnboarding color={ICON_COLOR} />}
            isDisabled={hiredApplications.length === 0}
          />
        )}{' '}
        {deleteAccess.isVisible && (
          <>
            <Menu.Separator />
            <Menu.Item
              variant="danger"
              onSelect={bulkDelete}
              label={t('deleteApplications')}
              icon={<IconTrash />}
              isDisabled={deleteAccess.isDisabled}
              tooltip={deleteAccess.tooltip}
            />
          </>
        )}
      </Menu>
      <Menu
        buttonProps={{
          variant: 'ghost',
          iconRight: <IconDropdown color={ICON_COLOR} />,
          iconLeft: <IconMove color={ICON_COLOR} />,
          text: t('moveCopy'),
        }}
      >
        <Menu.Item
          onSelect={bulkMoveToStage}
          label={t('moveToStage')}
          icon={<IconMove color={ICON_COLOR} />}
          iconRight={<IconStages color={ICON_COLOR} />}
        />
      </Menu>
      {hasIntegrations && (
        <Menu
          buttonProps={{
            variant: 'ghost',
            iconRight: <IconDropdown color={ICON_COLOR} />,
            iconLeft: <IconAppConnect color={ICON_COLOR} />,
            text: t('integrations'),
          }}
        >
          {hireflixEnabled && (
            <Menu.Item
              onSelect={bulkHireflixInvite}
              label={t('hireflixInvite')}
              icon={<IconBrandHireflix size={24} />}
            />
          )}
          {adManagerEnabled && (
            <Menu.Item
              onSelect={bulkADManagerCreateUser}
              label={t('adManagerUsers')}
              icon={<IconBrandADManager size={24} />}
            />
          )}
          {h3CoreEnabled && (
            <Menu.Item
              onSelect={() =>
                hiredApplications.length > 0 && h3NotCreated.length > 0
                  ? showH3BulkCreateSidebar({
                      applicants: h3NotCreated,
                    })
                  : null
              }
              label={t('h3Create')}
              icon={<IconBrandH3 size={24} />}
              isDisabled={hiredApplications.length === 0 || h3NotCreated.length === 0}
            />
          )}
          {h3CoreEnabled && (
            <Menu.Item
              onSelect={() =>
                hiredApplications.length > 0 && h3NotCreated.length > 0
                  ? showH3BulkEditSidebar({
                      applicants: h3NotCreated,
                    })
                  : null
              }
              label={t('h3Edit')}
              icon={<IconBrandH3 size={24} />}
              isDisabled={hiredApplications.length === 0 || h3NotCreated.length === 0}
            />
          )}
          {canViewJourneys && (
            <Menu.Item
              onSelect={() =>
                showJourneysCreateSidebar({
                  applications: selected.map((applicant) => applicant.id),
                  jobId: job.id,
                })
              }
              label={t('addToJourney')}
              icon={<IconBrandJourneys size={24} />}
              isDisabled={!isJourneysEnabled}
            />
          )}
        </Menu>
      )}
      <Menu
        buttonProps={{
          variant: 'ghost',
          iconRight: <IconDropdown color={ICON_COLOR} />,
          iconLeft: <IconStatus color={ICON_COLOR} />,
          text: t('updateStatus'),
        }}
      >
        <Menu.Item
          onSelect={() => (nonActiveApplications.length > 0 ? bulkActive() : null)}
          label={t('makeActive')}
          icon={<IconSend color="elementBlue100" />}
          isDisabled={nonActiveApplications.length === 0}
        />
        <Menu.Item
          onSelect={() => (activeApplications.length > 0 ? bulkRetract() : null)}
          label={t('retract')}
          icon={<IconUndo color="elementOrange100" />}
          isDisabled={activeApplications.length === 0}
        />
        <Menu.Item
          onSelect={() => (activeApplications.length > 0 ? bulkHire() : null)}
          label={t('hire')}
          icon={<IconTada color="elementGreen100" />}
          isDisabled={activeApplications.length === 0}
        />
        <Menu.Item
          onSelect={() => (rejectableApplications.length > 0 ? bulkReject() : null)}
          label={t('reject')}
          icon={<IconReject color="elementRed100" />}
          isDisabled={rejectableApplications.length === 0}
        />
      </Menu>
    </SelectionBanner>
  );
});

const locale = {
  en: {
    actions: 'Actions',
    sendEmail: 'Send email',
    grantAccess: 'Grant access',
    sendOnboarding: 'Send onboarding forms',
    moveCopy: 'Move / Copy',
    moveToStage: 'Move to stage',
    moveToJob: 'Move to job',
    copyToJob: 'Copy to job',
    integrations: 'Integrations',
    updateStatus: 'Update status',
    hire: 'Hire',
    reject: 'Reject',
    hireflixInvite: 'Send interviews',
    adManagerUsers: 'Create Users',
    h3Edit: 'Bulk edit H3',
    h3Create: 'Bulk create H3',
    addToJourney: 'Add to Journey',
    exportApplicants: 'Export applicants',
    retract: 'Retract',
    makeActive: 'Make active',
    deleteApplications: 'Delete applications',
  },
  is: {
    actions: 'Aðgerðir',
    sendEmail: 'Senda skilaboð',
    grantAccess: 'Gefa aðgang',
    sendOnboarding: 'Senda innleiðingarform',
    moveCopy: 'Færa / Afrita',
    moveToStage: 'Færa á milli dálka',
    integrations: 'Samþættingar',
    updateStatus: 'Uppfæra stöðu',
    hire: 'Ráða',
    reject: 'Hafna',
    hireflixInvite: 'Senda viðtöl',
    adManagerUsers: 'Stofna notendur',
    h3Edit: 'Breyta H3 gildum',
    h3Create: 'Stofna í H3',
    addToJourney: 'Bæta við í Journey',
    exportApplicants: 'Flytja út',
    retract: 'Draga til baka',
    makeActive: 'Gera virk',
    deleteApplications: 'Eyða umsóknum',
  },
};
